import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import { ChevronRightIcon } from "@heroicons/react/24/solid";
import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment Page404WithPopularPagesData on StrapiComponent404WithPopularPages {
    companyLogo {
      ...StrapiSharedLink
    }
    topic
    title
    description
    button {
      ...StrapiSharedLink
    }
    pageLinkSectionTitle
    pageLinks {
      ...StrapiSharedLink
    }
  }
`;

function WithPopularPages({ data }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="bg-background-white">
      <main className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {(data.companyLogo?.overwriteIcon || data.companyLogo?.link?.icon) && (
          <div className="flex-shrink-0 pt-16">
            <LinkWrapper
              to={data.companyLogo?.link?.url || "/"}
              className="inline-flex"
            >
              {(data.companyLogo?.overwriteTitle ||
                data.companyLogo?.link?.title) && (
                <span className="sr-only">
                  {data.companyLogo.overwriteTitle ||
                    data.companyLogo.link.title}
                </span>
              )}
              <MediaWrapper
                media={
                  data.companyLogo.overwriteIcon || data.companyLogo.link.icon
                }
                divClassName="mx-auto h-12 w-auto"
                className="mx-auto"
                objectFit="contain"
              />
            </LinkWrapper>
          </div>
        )}
        <div className="mx-auto max-w-xl py-16 sm:py-24">
          <div className="text-center">
            {data.topic && (
              <MarkdownWrapper
                text={data.topic}
                markdownClassName="block text-base text-center text-text-primary font-semibold tracking-wide uppercase"
              />
            )}
            {data.title && (
              <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-text-dark-gray sm:text-5xl">
                <MarkdownWrapper text={data.title} />
              </h1>
            )}
            {data.description && (
              <MarkdownWrapper
                text={data.description}
                markdownClassName="mt-2 text-lg text-text-gray"
              />
            )}
          </div>
          <div className="mt-12">
            {data.pageLinkSectionTitle && (
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-gray">
                <MarkdownWrapper text={data.pageLinkSectionTitle} />
              </h2>
            )}
            {Boolean(data.pageLinks?.length) && (
              <ul
                role="list"
                className="mt-4 divide-y divide-divide-light border-b border-t border-border-light-gray"
              >
                {data.pageLinks.map(
                  (pageLink, index) =>
                    pageLink.link?.url &&
                    (pageLink.overwriteTitle || pageLink.link?.title) && (
                      <li
                        key={index}
                        className="relative flex items-start space-x-4 py-6"
                      >
                        {(pageLink.overwriteIcon || pageLink.link.icon) && (
                          <div className="flex-shrink-0">
                            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-background-primary-light">
                              <MediaWrapper
                                media={
                                  pageLink.overwriteIcon || pageLink.link.icon
                                }
                                objectFit="contain"
                                divClassName="h-6 w-6 text-text-primary-dark"
                                aria-hidden="true"
                              />
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base font-medium text-text-dark-gray">
                            <span className="rounded-sm focus-within:ring-2 focus-within:ring-ring-primary focus-within:ring-offset-2">
                              <LinkWrapper
                                to={pageLink.link.url}
                                className="focus:outline-none"
                              >
                                <span
                                  className="absolute inset-0"
                                  aria-hidden="true"
                                />
                                {pageLink.overwriteTitle || pageLink.link.title}
                              </LinkWrapper>
                            </span>
                          </h3>
                          {(pageLink.overwriteDescription ||
                            pageLink.link.description) && (
                            <MarkdownWrapper
                              text={
                                pageLink.overwriteDescription ||
                                pageLink.link.description
                              }
                              markdownClassName="text-base text-text-gray"
                            />
                          )}
                        </div>
                        <div className="flex-shrink-0 self-center">
                          <ChevronRightIcon
                            className="h-5 w-5 text-text-gray"
                            aria-hidden="true"
                          />
                        </div>
                      </li>
                    )
                )}
              </ul>
            )}
            {(data.button?.overwriteTitle || data.button?.link?.title) && (
              <div className="mt-8">
                <LinkWrapper
                  to={data.button?.link?.url}
                  className="text-base font-medium text-text-primary hover:text-text-primary-dark"
                >
                  {data.button.overwriteTitle || data.button.link.title}
                  <span aria-hidden="true"> &rarr;</span>
                </LinkWrapper>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

WithPopularPages.propTypes = {
  data: PropTypes.object,
};

export default WithPopularPages;
